import config from "../../config.json";

const BASE_AUTH_API = config.BASE_AUTH_API;
const ENDPOINT = "billing";
const inFlightGetRequests = new Map();

const request = async ({ token, endpoint = "", method = "GET", body }) => {
  const url = `${BASE_AUTH_API}${ENDPOINT}${endpoint}`;
  const execute = async () => {
    const response = await fetch(url, {
      method,
      cache: "no-store",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      const error = new Error(
        data.message || data.error || "Billing request failed",
      );
      error.code = data.code;
      throw error;
    }
    return data;
  };

  if (method !== "GET") return execute();

  const requestKey = `${token}:${url}`;
  if (inFlightGetRequests.has(requestKey)) {
    return inFlightGetRequests.get(requestKey);
  }

  const pendingRequest = execute().finally(() => {
    inFlightGetRequests.delete(requestKey);
  });
  inFlightGetRequests.set(requestKey, pendingRequest);
  return pendingRequest;
};

const uploadRequest = async ({
  token,
  endpoint = "",
  method = "POST",
  formData,
}) => {
  const response = await fetch(`${BASE_AUTH_API}${ENDPOINT}${endpoint}`, {
    method,
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    const error = new Error(
      data.message || data.error || "Billing upload failed",
    );
    error.code = data.code;
    throw error;
  }
  return data;
};

const downloadRequest = async ({
  token,
  endpoint = "",
  filename = "download",
}) => {
  const response = await fetch(`${BASE_AUTH_API}${ENDPOINT}${endpoint}`, {
    method: "GET",
    cache: "no-store",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.message || data.error || "Download failed");
    error.code = data.code;
    throw error;
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const resolvedFilename = match?.[1] || filename;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = resolvedFilename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return { filename: resolvedFilename };
};

export const listBillingPlansAPI = (token, filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      query.set(key, value);
  });
  return request({
    token,
    endpoint: `/plans${query.toString() ? `?${query}` : ""}`,
  });
};

export const createBillingPlanAPI = (token, body) =>
  request({ token, endpoint: "/plans", method: "POST", body });

export const updateBillingPlanAPI = (token, id, body) =>
  request({ token, endpoint: `/plans/${id}`, method: "PATCH", body });

export const createBillingPlanVersionAPI = (token, id, body) =>
  request({
    token,
    endpoint: `/plans/${id}/new-version`,
    method: "POST",
    body,
  });

export const archiveBillingPlanAPI = (token, id) =>
  request({ token, endpoint: `/plans/${id}/archive`, method: "PATCH" });

export const listBillingStoresAPI = (token) =>
  request({ token, endpoint: "/restaurants" });

export const getBillingAccessAPI = (token) =>
  request({ token, endpoint: "/access" });

export const getBillingSettingsAPI = (token) =>
  request({ token, endpoint: "/settings" });

export const updateBillingSettingsAPI = (token, body) =>
  request({ token, endpoint: "/settings", method: "PATCH", body });

export const listAccessibleBillingRestaurantsAPI = (token) =>
  request({ token, endpoint: "/me/restaurants" });

export const getRestaurantBillingAPI = (token, filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      query.set(key, value);
  });
  return request({
    token,
    endpoint: `/me${query.toString() ? `?${query}` : ""}`,
  });
};

// Read-only subscription summary (plan, status, current period, remaining
// days) for one or more restaurants. The server applies billing access rules
// and silently omits restaurants outside the caller's scope.
export const getSubscriptionSummaryAPI = (token, storeIds = []) => {
  const query = new URLSearchParams();
  storeIds.filter(Boolean).forEach((id) => query.append("storeId", id));
  return request({
    token,
    endpoint: `/me/subscription-summary${query.toString() ? `?${query}` : ""}`,
  });
};

// Aggregated invoice + subscription figures for the LUD dashboard boxes,
// computed server-side across the restaurants the caller may see.
export const getDashboardBillingStatsAPI = (token, storeIds = []) => {
  const query = new URLSearchParams();
  storeIds.filter(Boolean).forEach((id) => query.append("storeId", id));
  return request({
    token,
    endpoint: `/me/dashboard-stats${query.toString() ? `?${query}` : ""}`,
  });
};

// Accountant page (LUD dashboard): read-only projections of Subscription /
// Invoice / Payment, scoped server-side to the caller's billing access and
// optionally to one restaurant (storeId).
const accountantQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      query.set(key, value);
  });
  return query.toString() ? `?${query}` : "";
};

export const getAccountantSubscriptionsAPI = (token, params) =>
  request({ token, endpoint: `/accountant/subscriptions${accountantQuery(params)}` });

export const getAccountantInvoicesAPI = (token, params) =>
  request({ token, endpoint: `/accountant/invoices${accountantQuery(params)}` });

export const getAccountantAnalyticsAPI = (token, params) =>
  request({ token, endpoint: `/accountant/analytics${accountantQuery(params)}` });

export const listSubscriptionsAPI = (token, filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      query.set(key, value);
  });
  return request({
    token,
    endpoint: `/subscriptions${query.toString() ? `?${query}` : ""}`,
  });
};

export const previewSubscriptionAPI = (token, body) =>
  request({ token, endpoint: "/subscriptions/preview", method: "POST", body });

export const createSubscriptionAPI = (token, body) =>
  request({ token, endpoint: "/subscriptions", method: "POST", body });

export const previewSelfServiceSubscriptionAPI = (token, body) =>
  request({
    token,
    endpoint: "/subscriptions/self-service/preview",
    method: "POST",
    body,
  });

export const createSelfServiceSubscriptionAPI = (token, body) =>
  request({
    token,
    endpoint: "/subscriptions/self-service",
    method: "POST",
    body,
  });

export const previewPlanChangeAPI = (token, id, body) =>
  request({
    token,
    endpoint: `/subscriptions/${id}/change-plan/preview`,
    method: "POST",
    body,
  });

export const schedulePlanChangeAPI = (token, id, body) =>
  request({
    token,
    endpoint: `/subscriptions/${id}/change-plan`,
    method: "POST",
    body,
  });

export const scheduleSubscriptionCancellationAPI = (token, id, body = {}) =>
  request({
    token,
    endpoint: `/subscriptions/${id}/cancel`,
    method: "POST",
    body,
  });

export const cancelSubscriptionNowAPI = (token, id, body) =>
  request({
    token,
    endpoint: `/subscriptions/${id}/cancel-now`,
    method: "POST",
    body,
  });

export const listInvoicesAPI = (token, filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      query.set(key, value);
  });
  return request({
    token,
    endpoint: `/invoices${query.toString() ? `?${query}` : ""}`,
  });
};

export const getInvoiceAPI = (token, id) =>
  request({ token, endpoint: `/invoices/${id}` });

export const getInvoiceHistoryAPI = (token, id) =>
  request({ token, endpoint: `/invoices/${id}/history` });

export const getSubscriptionHistoryAPI = (token, id) =>
  request({ token, endpoint: `/subscriptions/${id}/history` });

export const addSubscriptionBranchAPI = (token, id, branchId, body) =>
  request({
    token,
    endpoint: `/subscriptions/${id}/branches/${encodeURIComponent(branchId)}/add`,
    method: "POST",
    body,
  });

export const extendSubscriptionBranchAPI = (token, id, branchId, body) =>
  request({
    token,
    endpoint: `/subscriptions/${id}/branches/${encodeURIComponent(branchId)}/extend`,
    method: "POST",
    body,
  });

export const stopSubscriptionBranchAPI = (token, id, branchId, body = {}) =>
  request({
    token,
    endpoint: `/subscriptions/${id}/branches/${encodeURIComponent(branchId)}/stop`,
    method: "POST",
    body,
  });

export const extendAllSubscriptionBranchesAPI = (token, storeId, body) =>
  request({
    token,
    endpoint: `/restaurants/${encodeURIComponent(storeId)}/subscriptions/extend-all`,
    method: "POST",
    body,
  });

export const stopAllSubscriptionBranchesAPI = (token, storeId, body = {}) =>
  request({
    token,
    endpoint: `/restaurants/${encodeURIComponent(storeId)}/subscriptions/stop-all`,
    method: "POST",
    body,
  });

export const downloadInvoicePdfAPI = (token, id, lang = "ar") =>
  downloadRequest({
    token,
    endpoint: `/invoices/${id}/pdf?lang=${encodeURIComponent(lang === "en" ? "en" : "ar")}`,
    filename: "invoice.pdf",
  });

export const createInvoiceAPI = (token, body) =>
  request({ token, endpoint: "/invoices", method: "POST", body });

export const issueInvoiceAPI = (token, id, body = {}) =>
  request({ token, endpoint: `/invoices/${id}/issue`, method: "POST", body });

export const voidInvoiceAPI = (token, id, body) =>
  request({ token, endpoint: `/invoices/${id}/void`, method: "POST", body });

export const getPaymentReceiptUrlAPI = (token, id) =>
  request({ token, endpoint: `/payments/${id}/receipt-url` });

export const createManualPaymentAPI = (token, body, file) => {
  if (!file)
    return request({
      token,
      endpoint: "/payments/manual",
      method: "POST",
      body,
    });
  const formData = new FormData();
  Object.entries(body || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      formData.set(key, value);
  });
  formData.set("file", file);
  return uploadRequest({ token, endpoint: "/payments/manual", formData });
};

export const uploadInvoiceReceiptAPI = (token, invoiceId, body, file) => {
  const formData = new FormData();
  Object.entries(body || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      formData.set(key, value);
  });
  if (file) formData.set("file", file);
  return uploadRequest({
    token,
    endpoint: `/invoices/${invoiceId}/receipt`,
    formData,
  });
};

export const listServiceCatalogAPI = (token) =>
  request({ token, endpoint: "/service-catalog" });

export const createServiceOrderAPI = (token, body) =>
  request({ token, endpoint: "/service-orders", method: "POST", body });

export const listMyServiceOrdersAPI = (token, filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      query.set(key, value);
  });
  return request({
    token,
    endpoint: `/service-orders${query.toString() ? `?${query}` : ""}`,
  });
};

export const approvePaymentAPI = (token, id) =>
  request({ token, endpoint: `/payments/${id}/approve`, method: "POST" });

export const rejectPaymentAPI = (token, id, body) =>
  request({ token, endpoint: `/payments/${id}/reject`, method: "POST", body });

export const cancelPaymentAPI = (token, id, body) =>
  request({ token, endpoint: `/payments/${id}/cancel`, method: "POST", body });

const withQuery = (endpoint, filters = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  return `${endpoint}${query.toString() ? `?${query}` : ""}`;
};

export const createStandaloneInvoiceAPI = (token, body) =>
  request({ token, endpoint: "/invoices/standalone", method: "POST", body });

export const getInvoiceAnalyticsAPI = (token, filters = {}) =>
  request({ token, endpoint: withQuery("/invoices/analytics", filters) });

export const getInvoiceLedgerAPI = (token, filters = {}) =>
  request({ token, endpoint: withQuery("/invoices/ledger", filters) });

export const getSubscriptionsOverviewAPI = (token) =>
  request({ token, endpoint: "/subscriptions/overview" });

export const getSubscriptionsLedgerAPI = (token, filters = {}) =>
  request({ token, endpoint: withQuery("/subscriptions/ledger", filters) });

export const listScheduledStopsAPI = (token, filters = {}) =>
  request({ token, endpoint: withQuery("/subscriptions/scheduled-stops", filters) });

export const getSettingsSnapshotAPI = (token) =>
  request({ token, endpoint: "/settings/snapshot" });

export const listCouponsAPI = (token) => request({ token, endpoint: "/coupons" });

export const createCouponAPI = (token, body) =>
  request({ token, endpoint: "/coupons", method: "POST", body });

export const updateCouponAPI = (token, id, body) =>
  request({ token, endpoint: `/coupons/${id}`, method: "PATCH", body });

export const applyCouponToInvoiceAPI = (token, invoiceId, body) =>
  request({ token, endpoint: `/invoices/${invoiceId}/apply-coupon`, method: "POST", body });

export const listServiceCatalogAdminAPI = (token) =>
  request({ token, endpoint: "/service-catalog/admin" });

export const createServiceCatalogItemAPI = (token, body) =>
  request({ token, endpoint: "/service-catalog/admin", method: "POST", body });

export const updateServiceCatalogItemAPI = (token, id, body) =>
  request({ token, endpoint: `/service-catalog/admin/${id}`, method: "PATCH", body });

export const getServiceFulfillmentQueueAPI = (token) =>
  request({ token, endpoint: "/service-orders/fulfillment-queue" });

export const transitionServiceOrderFulfillmentAPI = (token, id, body) =>
  request({ token, endpoint: `/service-orders/${id}/fulfillment-transition`, method: "POST", body });

// Read-only consistency review of the billing estate (global administrators; others get 403).
export const getBillingReconciliationAPI = (token) =>
  request({ token, endpoint: "/admin/reconciliation" });

// Administrator-granted subscriptions (activated without payment): global administrators only; the server enforces it.
export const grantSubscriptionAPI = (token, body) =>
  request({ token, endpoint: "/subscriptions/grant", method: "POST", body });

export const extendGrantAPI = (token, id, body) =>
  request({ token, endpoint: `/subscriptions/${id}/grant/extend`, method: "POST", body });

export const convertGrantToPaidAPI = (token, id, body) =>
  request({ token, endpoint: `/subscriptions/${id}/grant/convert`, method: "POST", body });
